<script lang="ts">
    import { onMount } from "svelte";
    import { get } from "svelte/store";
    import { meta } from "tinro";
    import { router } from "tinro";

    const route = meta();

    import { derived } from "svelte/store";
    import {
        databases,
        filteredDatabases,
        letterFilteredDatabases,
        isLoadingDatabases
    } from "../lib/data/databases.js";

    import {
        searchTerm,
        searchedByLetter,
        showSummary,
        databasesNotFound,
        byPeer,
        clickedLetter
    } from "../lib/data/stores.js";

    import {sendGAEvent} from '../lib/utilities/ga.js';

    import FilterBar from "../lib/components/FilterBar.svelte";
    import SearchField from "../lib/components/SearchField.svelte";
    import Button from "../lib/components/Button.svelte";
    import SearchSummary from "../lib/components/SearchSummary.svelte";
    import DatabaseList from "../lib/components/DatabaseList.svelte";
    import Checkbox from "../lib/components/Checkbox.svelte";
    import NoResults from "../lib/components/NoResults.svelte";
    import TextBlock from "../lib/components/TextBlock.svelte";
    import LetterLinks from "../lib/components/LetterLinks.svelte";
    import Spinner from "../lib/components/Spinner.svelte";

    export let content = "";

    let mounted = false;

    /**
     * Reactive block to manage query params based on store changes.
     */
    $: {
        if (!$searchTerm) {
            router.location.query.delete("searchTerm");
        } else {
            router.location.query.set("searchTerm", encodeURIComponent($searchTerm));
            letterFilteredDatabases.set([]);
            clickedLetter.set("");
        }

        if (!$clickedLetter) {
            router.location.query.delete("clickedLetter");
            router.location.query.delete("searchedByLetter");
        } else {
            router.location.query.set("clickedLetter", $clickedLetter);
            router.location.query.set("searchedByLetter", "true");
        }

        if (!$byPeer) {
            router.location.query.delete("byPeer");
        } else {
            router.location.query.set("byPeer", "true");
        }
    }

    /**
     * Reactive block to listen for changes in router query params
     * and update the Svelte stores accordingly.
     */
    $: {
        const params = router.location.query;
        if (params.get("searchTerm")) {
            searchTerm.set(decodeURIComponent(params.get("searchTerm")));
        } else {
            searchTerm.set("");
        }
        clickedLetter.set(params.get("clickedLetter") || "");
        searchedByLetter.set(params.get("searchedByLetter") === "true");
        byPeer.set(params.get("byPeer") === "true");
    }

    /**
     * onMount: Add a hashchange listener and do initial search.
     */
    onMount(() => {
        window.addEventListener("hashchange", () => {
            const params = new URLSearchParams(window.location.hash.slice(1));
            searchTerm.set(decodeURIComponent(params.get("searchTerm")) || "");
            clickedLetter.set(params.get("clickedLetter") || "");
            searchedByLetter.set(params.get("searchedByLetter") || "");
            byPeer.set(params.get("byPeer") === "true");
        });

        console.log("searchTerm: ", $searchTerm);
        searchDatabases();
        mounted = true;
    });

    /**
     * Keep a simple subscription so filteredDatabases is initially populated.
     */
    databases.subscribe((dbs) => {
        filteredDatabases.set(dbs);
    });

    /**
     * Removes the nested subscriptions inside searchDatabases()
     * and replaces them with get(...) calls to avoid multiple triggers.
     */
    function searchDatabases() {
        const isLoading = get(isLoadingDatabases);
        if (!isLoading) {
            let dbs = get(databases);
            let filtered = dbs;

            const term = get(searchTerm);
            const letter = get(clickedLetter);
            const peer = get(byPeer);

            if (term && !letter) {
                filtered = filtered.filter((database) =>
                    database.title.toLowerCase().includes(term.toLowerCase())
                );
            }

            if (!term && letter) {
                filtered = filtered.filter((database) =>
                    database.title.toLowerCase().startsWith(letter.toLowerCase())
                );
                showSummary.set(true);
                searchedByLetter.set(true);
            }

            if (peer) {
                filtered = filtered.filter((database) => database.peer === "true");
            }

            filteredDatabases.set(filtered);

            if (term && !letter) {
                sendGAEvent("AllDatabases", "search", `Term: ${term}`, route.match);
            }

            if (filtered.length === 0) {
                databasesNotFound.set(true);
                searchedByLetter.set(false);
            } else {
                databasesNotFound.set(false);
                searchedByLetter.set(false);
            }

            showSummary.set(true);
        }
    }

    function clearFilter() {
        showSummary.set(false);
        databasesNotFound.set(false);
        searchTerm.set("");
        letterFilteredDatabases.set([]);
        clickedLetter.set("");
        router.location.query.delete("searchTerm");
        router.location.query.delete("clickedLetter");
        router.location.query.delete("byPeer");

        searchDatabases();

        sendGAEvent("AllDatabases", "clear_filter", "All filters cleared", route.match);
    }

    function filterByPeer() {
        $byPeer = !$byPeer;
        showSummary.set(true);
        searchDatabases();

        sendGAEvent(
            "AllDatabases",
            "filter_peer",
            $byPeer ? "enabled" : "disabled",
            route.match
        );
    }

    function handleLetterClick(event) {
        const letter = event.detail;
        if (letter !== "all") {
            searchTerm.set("");
            clickedLetter.set(letter);
            searchedByLetter.set(true);
            showSummary.set(true);

            sendGAEvent(
                "AllDatabases",
                "letter_click",
                `Letter: ${letter}`,
                route.match
            );
        } else {
            clearFilter();
            showSummary.set(false);
            searchTerm.set("");
            clickedLetter.set("");

            sendGAEvent("AllDatabases", "letter_click", "all", route.match);
        }
        searchDatabases();
    }

    const letterFilter = derived(databases, ($databases) => {
        const letters = {};
        $databases.forEach((database) => {
            const letter = database.title.charAt(0).toUpperCase();
            if (!letters[letter]) {
                letters[letter] = true;
            }
        });

        const letterFilterArr = Object.entries(letters).map(([letter, active]) => ({
            letter,
            active
        }));

        console.log("letterFilterArr:", letterFilterArr);
        return letterFilterArr;
    }, []);
</script>

<FilterBar parent_id="all-databases">
    <TextBlock
            classes="mb-3 text-justify"
            title={content.label}
            text={content.heading}
    />

    <form
            id="databaseFilter"
            class="form-inline d-flex flex-md-nowrap justify-content-lg-start"
    >
        <div class="form-group d-flex">
            <SearchField
                    tabindex="0"
                    fieldlabel="Enter database name"
                    id="filterControl"
                    placeholder="Enter database name"
                    bind:value="{$searchTerm}"
                    on:searchtext="{searchDatabases}"
                    items="{$filteredDatabases}"
            />
            <Button
                    on:click={clearFilter}
                    label="clear"
                    classes="btn btn-info small"
            />
        </div>
        <div class="form-check ml-sm-2 p-0">
            <Checkbox
                    id="by-peer"
                    label="Peer Reviewed Content"
                    bind:checked="{$byPeer}"
                    on:click={filterByPeer}
            />
        </div>
    </form>

    <LetterLinks
            on:click="{handleLetterClick}"
            letterFilterArr="{$letterFilter}"
    />
</FilterBar>

{#if $isLoadingDatabases}
    <Spinner/>
{/if}

{#if $showSummary && ($searchTerm || $clickedLetter || $byPeer)}
    <div class="row">
        <SearchSummary
                count="{$filteredDatabases.length}"
                searchTerm="{$searchTerm}"
                clickedLetter="{$clickedLetter}"
                searchedByLetter="{$searchedByLetter}"
                byPeer="{$byPeer}"
                on:copyToClipboard
        />
    </div>
{/if}

{#if $filteredDatabases.length > 0}
    <DatabaseList
            on:openDialog
            bind:databases="{$filteredDatabases}"
    />
{:else}
    <NoResults/>
{/if}

<style>
</style>
